import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import api from "../api";
import { resolveAssetUrl } from "../utils/assets";
import { sanitizeDocumentTemplateHtml } from "../security/documentTemplateHtml";
import { findSafePageCut } from "./documentPdfPagination";
import { describeDocumentFormula, evaluateDocumentFormula, parseDocumentFormula } from "../features/documentTemplateFormula";

const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

// Compatibilidade temporária com o preenchimento de CEP ainda existente no
// ProcessRunner. O helper permanece local ao serviço e também é exposto no
// escopo global carregado pelo bundle, sem alterar o comportamento da função.
if (typeof globalThis !== "undefined") {
  globalThis.digitsOnly = digitsOnly;
}

const formatCnpj = (value) => {
  const digits = digitsOnly(value).slice(0, 14);
  if (digits.length !== 14) return value || "";
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
};

const drawDocumentInfoIcon = (doc, type, x, y, size = 8) => {
  doc.setDrawColor(55, 65, 81);
  doc.setFillColor(55, 65, 81);
  doc.setLineWidth(0.8);
  const midY = y - size / 2;
  if (type === "email") {
    doc.rect(x, y - size + 1, size, size - 2);
    doc.line(x, y - size + 1, x + size / 2, midY + 1);
    doc.line(x + size, y - size + 1, x + size / 2, midY + 1);
  } else if (type === "phone") {
    const scale = size / 512;
    const left = x;
    const top = y - size;
    const point = (px, py) => [left + px * scale, top + py * scale];
    doc.path([
      { op: "m", c: point(493.4, 24.6) },
      { op: "l", c: point(389.4, 0.6) },
      { op: "c", c: [...point(378.1, -2.0), ...point(366.5, 3.9), ...point(361.9, 14.5)] },
      { op: "l", c: point(313.9, 126.5) },
      { op: "c", c: [...point(309.7, 136.3), ...point(312.5, 147.8), ...point(320.8, 154.5)] },
      { op: "l", c: point(381.4, 204.1) },
      { op: "c", c: [...point(345.4, 280.8), ...point(282.5, 344.6), ...point(204.2, 381.3)] },
      { op: "l", c: point(154.6, 320.7) },
      { op: "c", c: [...point(147.8, 312.4), ...point(136.4, 309.6), ...point(126.6, 313.8)] },
      { op: "l", c: point(14.6, 361.8) },
      { op: "c", c: [...point(3.9, 366.5), ...point(-2.0, 378.1), ...point(0.6, 389.4)] },
      { op: "l", c: point(24.6, 493.4) },
      { op: "c", c: [...point(27.1, 504.2), ...point(36.7, 512.0), ...point(48.0, 512.0)] },
      { op: "c", c: [...point(304.1, 512.0), ...point(512.0, 304.5), ...point(512.0, 48.0)] },
      { op: "c", c: [...point(512.0, 36.8), ...point(504.3, 27.1), ...point(493.4, 24.6)] },
      { op: "h", c: [] },
    ]).fill();
  } else if (type === "site") {
    const centerX = x + size / 2;
    const centerY = midY + 0.5;
    doc.circle(centerX, centerY, size / 2);
    doc.ellipse(centerX, centerY, size / 4, size / 2);
    doc.line(x + 1, centerY, x + size - 1, centerY);
    doc.line(centerX, y - size + 1, centerX, y);
  } else if (type === "address") {
    doc.triangle(x + size / 2, y - size, x, y - size / 2, x + size, y - size / 2, "F");
    doc.rect(x + 1.2, y - size / 2, size - 2.4, size / 2);
    doc.rect(x + size / 2 - 1.2, y - size / 2 + 2, 2.4, size / 2 - 2);
  } else if (type === "arrow") {
    doc.triangle(x, y - size, x, y + 1, x + size * 0.68, y - size * 0.2, "F");
    doc.setDrawColor(255, 255, 255);
    doc.line(x + size * 0.28, y - size * 0.35, x + size * 0.52, y + 1);
    doc.setDrawColor(55, 65, 81);
  }
};

const drawDocumentIconText = (doc, type, text, x, y, options = {}) => {
  const iconSize = options.iconSize || 8;
  drawDocumentInfoIcon(doc, type, x, y, iconSize);
  doc.text(text, x + iconSize + 5, y, options.textOptions || {});
  return x + iconSize + 5 + doc.getTextWidth(text);
};

const escapeDocumentHtml = (value = "") =>
  String(value).replace(/[&<>"']/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[char]));

const formatDocumentDateValue = (value) => {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/);
  if (!match) return null;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const formatDocumentValue = (value) => {
  if (value === undefined || value === null || value === "") return "";
  if (Array.isArray(value)) return value.map(formatDocumentValue).filter(Boolean).join(", ");
  if (typeof value === "object") return value.name || value.label || value.displayValue || JSON.stringify(value);
  return formatDocumentDateValue(value) || String(value);
};

const formatDocumentHtmlValue = (value) =>
  escapeDocumentHtml(formatDocumentValue(value)).replace(/\r?\n/g, "<br>");

const normalizeDocumentVariableKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const DOCUMENT_PDF_RENDER_SCALE = 2;
const DOCUMENT_PDF_JPEG_QUALITY = 0.92;

const resolveDocumentAssetUrl = (url) => {
  if (!url) return "";
  if (/^data:/i.test(url)) return url;
  return resolveAssetUrl(url) || url;
};

const readBlobAsDataUrl = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });

const rasterizeImageDataUrl = (source) =>
  new Promise((resolve) => {
    if (!source) return resolve(null);
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth || image.width;
        canvas.height = image.naturalHeight || image.height;
        canvas.getContext("2d").drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = source;
  });

const loadDocumentImageDataUrl = async (url) => {
  const resolvedUrl = resolveDocumentAssetUrl(url);
  if (!resolvedUrl) return null;
  if (/^data:image\/(png|jpe?g|webp)/i.test(resolvedUrl)) return resolvedUrl;
  if (/^data:image\/svg\+xml/i.test(resolvedUrl)) return rasterizeImageDataUrl(resolvedUrl);
  try {
    const response = await api.get(resolvedUrl, { responseType: "blob" });
    const dataUrl = await readBlobAsDataUrl(response.data);
    if (response.data?.type?.includes("svg") || /^data:image\/svg\+xml/i.test(dataUrl || "")) {
      return await rasterizeImageDataUrl(dataUrl);
    }
    return dataUrl;
  } catch {
    return rasterizeImageDataUrl(resolvedUrl);
  }
};

const waitForDocumentImages = async (element) => {
  const images = Array.from(element?.querySelectorAll?.("img") || []);
  await Promise.all(
    images.map(async (image) => {
      if (image.complete && image.naturalWidth > 0) return;
      if (typeof image.decode === "function") {
        try {
          await image.decode();
          if (image.naturalWidth > 0) return;
        } catch {
          // Alguns navegadores rejeitam decode() antes do evento load.
        }
      }
      await new Promise((resolve) => {
        const finish = () => resolve();
        image.addEventListener("load", finish, { once: true });
        image.addEventListener("error", finish, { once: true });
        window.setTimeout(finish, 3000);
      });
    }),
  );
};

const replaceDocumentImagesWithCanvas = async (element) => {
  const images = Array.from(element?.querySelectorAll?.("img") || []);
  await Promise.all(
    images.map(async (image) => {
      if (!(image.complete && image.naturalWidth > 0)) {
        if (typeof image.decode === "function") {
          try {
            await image.decode();
          } catch {
            // O fallback por eventos logo abaixo cobre navegadores que rejeitam decode().
          }
        }
      }
      if (!(image.complete && image.naturalWidth > 0)) {
        await new Promise((resolve) => {
          const finish = () => resolve();
          image.addEventListener("load", finish, { once: true });
          image.addEventListener("error", finish, { once: true });
          window.setTimeout(finish, 3500);
        });
      }
      if (!image.naturalWidth || !image.naturalHeight) return;

      const rect = image.getBoundingClientRect();
      const cssWidth = Math.max(1, rect.width || image.naturalWidth);
      const cssHeight = Math.max(1, rect.height || (cssWidth * image.naturalHeight) / image.naturalWidth);
      const scale = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(cssWidth * scale));
      canvas.height = Math.max(1, Math.round(cssHeight * scale));
      canvas.style.cssText = image.style.cssText;
      canvas.style.width = `${cssWidth}px`;
      canvas.style.height = `${cssHeight}px`;
      canvas.style.maxWidth = "100%";
      canvas.style.objectFit = "contain";
      const context = canvas.getContext("2d");
      if (!context) return;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      image.replaceWith(canvas);
    }),
  );
};

const renderHtmlElementToPdf = async (doc, element, {
  x,
  y,
  width,
  pageHeight,
  topMargin,
  bottomMargin = 60,
  getNextPageStartY,
}) => {
  await waitForDocumentImages(element);
  await replaceDocumentImagesWithCanvas(element);
  const canvas = await html2canvas(element, {
    scale: DOCUMENT_PDF_RENDER_SCALE,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: element.scrollWidth,
    windowHeight: element.scrollHeight,
  });
  const imageWidth = width;
  const canvasToPdfRatio = imageWidth / canvas.width;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  const rowHasInk = (row) => {
    const pixels = context.getImageData(0, row, canvas.width, 1).data;
    const step = Math.max(1, Math.floor(canvas.width / 120));
    let ink = 0;
    for (let column = 0; column < canvas.width; column += step) {
      const index = column * 4;
      if (pixels[index + 3] > 20 && (pixels[index] < 245 || pixels[index + 1] < 245 || pixels[index + 2] < 245)) {
        ink += 1;
        if (ink > 1) return true;
      }
    }
    return false;
  };
  const paintedRows = Array.from({ length: canvas.height }, (_, row) => rowHasInk(row));
  let contentBottom = 0;
  for (let row = canvas.height - 1; row >= 0; row -= 1) {
    if (paintedRows[row]) {
      const trailingSafetyRows = 8;
      contentBottom = Math.min(canvas.height, row + 1 + trailingSafetyRows);
      break;
    }
  }
  if (!contentBottom) return;
  let sourceY = 0;
  const findSafeCutY = (desiredHeight) => {
    const minHeight = Math.max(1, Math.floor(desiredHeight * 0.45));
    const start = Math.max(sourceY + minHeight, sourceY);
    const end = Math.min(sourceY + desiredHeight, contentBottom);
    if (end >= contentBottom) return contentBottom - sourceY;
    const absoluteCut = findSafePageCut({
      paintedRows,
      targetRow: end,
      minRow: start,
      safetyRows: 6,
    });
    const relativeCut = Math.max(1, absoluteCut - sourceY);
    return Math.min(relativeCut, desiredHeight);
  };
  let targetY = y;
  let availableHeight = pageHeight - targetY - bottomMargin;

  while (sourceY < contentBottom) {
    if (availableHeight <= 0) {
      doc.addPage();
      targetY = getNextPageStartY ? getNextPageStartY() : topMargin;
      availableHeight = pageHeight - targetY - bottomMargin;
    }

    const desiredSliceHeight = Math.min(contentBottom - sourceY, Math.floor(availableHeight / canvasToPdfRatio));
    const sourceSliceHeight = findSafeCutY(desiredSliceHeight);
    const pageCanvas = document.createElement("canvas");
    pageCanvas.width = canvas.width;
    pageCanvas.height = sourceSliceHeight;
    const pageContext = pageCanvas.getContext("2d");
    pageContext.drawImage(canvas, 0, sourceY, canvas.width, sourceSliceHeight, 0, 0, canvas.width, sourceSliceHeight);
    const sliceHeight = sourceSliceHeight * canvasToPdfRatio;
    const imageData = pageCanvas.toDataURL("image/jpeg", DOCUMENT_PDF_JPEG_QUALITY);
    doc.addImage(imageData, "JPEG", x, targetY, imageWidth, sliceHeight, undefined, "MEDIUM");

    sourceY += sourceSliceHeight;
    if (sourceY < contentBottom) {
      doc.addPage();
      targetY = getNextPageStartY ? getNextPageStartY() : topMargin;
      availableHeight = pageHeight - targetY - bottomMargin;
    }
  }
};

export const generateDocumentPdf = async ({
  field,
  fields = [],
  values = {},
  instance,
  company: providedCompany,
  preview = false,
  openedPdfWindow,
  providedDocument = null,
  save = true,
  addPageBefore = false,
} = {}) => {
  let pdfOpened = false;
  try {
    const template = field?.documentTemplate || {};
    const doc = providedDocument || new jsPDF({ unit: "pt", format: "a4" });
    if (providedDocument && addPageBefore) doc.addPage();
    const firstPageIndex = doc.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 48;
    let company = providedCompany || null;
    if (!providedCompany) {
      try {
        const res = await api.get("/company");
        company = res.data;
      } catch {
        company = null;
      }
    }

    const companyLogoUrl = company?.logoUrl || company?.logo || company?.primaryLogoPath;
    const logoDataUrl = companyLogoUrl ? await loadDocumentImageDataUrl(companyLogoUrl) : null;
    if (companyLogoUrl && !logoDataUrl) {
      throw new Error("Não foi possível carregar a logo configurada para a empresa.");
    }

    const fieldList = Array.isArray(fields) ? fields : [];
    const fieldMap = new Map(
      fieldList
        .filter((item) => item?.id)
        .map((item) => [String(item.id), item]),
    );
    const normalizedIdMap = new Map();
    const normalizedLabelMap = new Map();

    fieldList.forEach((item) => {
      if (!item?.id) return;
      const id = String(item.id);
      const normalizedId = normalizeDocumentVariableKey(id);
      if (normalizedId && !normalizedIdMap.has(normalizedId)) {
        normalizedIdMap.set(normalizedId, id);
      }
      const normalizedLabel = normalizeDocumentVariableKey(item.label);
      if (!normalizedLabel) return;
      const ids = normalizedLabelMap.get(normalizedLabel) || [];
      if (!ids.includes(id)) ids.push(id);
      normalizedLabelMap.set(normalizedLabel, ids);
    });

    const hasValueKey = (key) =>
      Object.prototype.hasOwnProperty.call(values || {}, key);

    const resolveDocumentVariable = (key) => {
      const raw = String(key || "").trim();
      if (!raw) return { id: "", meta: null, label: "" };

      if (fieldMap.has(raw) || hasValueKey(raw)) {
        return { id: raw, meta: fieldMap.get(raw) || null, label: fieldMap.get(raw)?.label || raw };
      }

      const normalized = normalizeDocumentVariableKey(raw);
      if (normalized && hasValueKey(normalized)) {
        return {
          id: normalized,
          meta: fieldMap.get(normalized) || null,
          label: fieldMap.get(normalized)?.label || raw,
        };
      }

      const normalizedId = normalizedIdMap.get(normalized);
      if (normalizedId) {
        const meta = fieldMap.get(normalizedId) || null;
        return { id: normalizedId, meta, label: meta?.label || raw };
      }

      const labelMatches = normalizedLabelMap.get(normalized) || [];
      if (labelMatches.length === 1) {
        const id = labelMatches[0];
        const meta = fieldMap.get(id) || null;
        return { id, meta, label: meta?.label || raw };
      }

      return { id: raw, meta: null, label: raw };
    };

    const replaceToken = (key) => {
      const formula = parseDocumentFormula(key);
      if (formula) {
        return preview
          ? describeDocumentFormula(key, fieldList)
          : evaluateDocumentFormula(key, values, (formulaVariableId) => {
              const resolvedFormulaVariable = resolveDocumentVariable(formulaVariableId);
              return values?.[resolvedFormulaVariable.id];
            });
      }
      const resolved = resolveDocumentVariable(key);
      const rendered = preview
        ? resolved.label
        : formatDocumentValue(values?.[resolved.id]);
      return rendered || "";
    };
    const interpolateText = (text) =>
      String(text || "").replace(/{{\s*([^}]+)\s*}}/g, (_, key) => replaceToken(key));
    const interpolateHtml = (html) =>
      sanitizeDocumentTemplateHtml(html).replace(/{{\s*([^}]+)\s*}}/g, (_, key) => {
        const rendered = replaceToken(key);
        const safeValue = rendered ? formatDocumentHtmlValue(rendered) : "";
        const tokenColor = preview ? "#dc2626" : "inherit";
        const tokenWeight = preview ? "700" : "inherit";
        return `<span style="color:${tokenColor};font-weight:${tokenWeight};">${safeValue}</span>`;
      });

    const addFooter = () => {
      const total = doc.getNumberOfPages();
      const footerText = "Documento gerado pelo sistema Intelligent BPM Nash.";
      const documentPageTotal = total - firstPageIndex + 1;
      for (let index = firstPageIndex; index <= total; index += 1) {
        doc.setPage(index);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(40);
        const footerWidth = doc.getTextWidth(footerText);
        const footerX = (pageWidth - footerWidth) / 2;
        const footerY = pageHeight - 26;
        drawDocumentInfoIcon(doc, "site", footerX - 15, footerY, 9);
        doc.text(footerText, footerX, footerY);
        doc.link(footerX, footerY - 10, footerWidth, 12, { url: "https://www.nashtec.com.br" });
        drawDocumentInfoIcon(doc, "arrow", footerX + footerWidth + 7, footerY, 9);
        doc.text(`${index - firstPageIndex + 1}/${documentPageTotal}`, pageWidth - margin, footerY, { align: "right" });
      }
    };

    const companyName = company?.name || company?.tradeName || "Dados cadastrais da empresa";
    const companyCnpj = company?.taxId ? `CNPJ: ${formatCnpj(company.taxId)}` : "";
    const companyAddress = [company?.addressLine1, company?.addressLine2, company?.city, company?.state, company?.zipCode]
      .filter(Boolean)
      .join(" • ");
    const companyPrimaryContacts = [
      { type: "phone", value: company?.phone },
      { type: "email", value: company?.email },
    ].filter((item) => item.value);
    const companyWebsite = company?.website;
    const logoBoxWidth = logoDataUrl ? 82 : 0;

    const drawHeader = ({ includeTitle = false } = {}) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(40);
      if (logoDataUrl) {
        const props = doc.getImageProperties(logoDataUrl);
        const scale = Math.min(72 / props.width, 58 / props.height);
        doc.addImage(
          logoDataUrl,
          props.fileType || (logoDataUrl.includes("image/jpeg") ? "JPEG" : "PNG"),
          margin,
          margin - 12,
          props.width * scale,
          props.height * scale,
        );
      }
      const companyTextX = margin + logoBoxWidth;
      let headerY = margin;
      doc.setFont("helvetica", "bold");
      doc.text(companyName, companyTextX, headerY);
      doc.setFont("helvetica", "normal");
      headerY += 14;
      if (companyCnpj) {
        doc.text(companyCnpj, companyTextX, headerY);
        headerY += 14;
      }
      if (companyAddress) {
        drawDocumentIconText(doc, "address", companyAddress, companyTextX, headerY);
        headerY += 14;
      }
      if (companyPrimaryContacts.length) {
        let contactX = companyTextX;
        companyPrimaryContacts.forEach((item, index) => {
          if (index > 0) {
            doc.text("•", contactX + 4, headerY);
            contactX += 14;
          }
          contactX = drawDocumentIconText(doc, item.type, item.value, contactX, headerY) + 8;
        });
        headerY += 14;
      }
      if (companyWebsite) {
        const websiteIconSize = 8;
        const websiteTextX = companyTextX + websiteIconSize + 5;
        const websiteMaxWidth = pageWidth - margin - websiteTextX;
        const websiteLines = doc.splitTextToSize(String(companyWebsite), websiteMaxWidth);
        const websiteLineCount = Array.isArray(websiteLines) ? websiteLines.length : 1;
        drawDocumentInfoIcon(doc, "site", companyTextX, headerY, websiteIconSize);
        doc.text(websiteLines, websiteTextX, headerY);
        headerY += Math.max(1, websiteLineCount) * 14;
      }
      const separatorY = Math.max(headerY, margin + 56) + 24;
      doc.setDrawColor(210);
      doc.line(margin, separatorY, pageWidth - margin, separatorY);
      if (!includeTitle) return separatorY + 24;
      const titleY = separatorY + 34;
      doc.setFontSize(16);
      doc.setFont(undefined, "bold");
      doc.setTextColor(20);
      const titleText = interpolateText(template.title || field?.label || "Documento");
      const titleLines = doc.splitTextToSize(titleText, pageWidth - margin * 2);
      doc.text(titleLines, pageWidth / 2, titleY, { align: "center" });
      doc.setFont(undefined, "normal");
      return titleY + titleLines.length * 19;
    };

    const y = drawHeader({ includeTitle: true });
    const maxWidth = pageWidth - margin * 2;
    const documentRoot = document.createElement("div");
    documentRoot.style.position = "absolute";
    documentRoot.style.left = "-10000px";
    documentRoot.style.top = "0";
    documentRoot.style.zIndex = "1";
    documentRoot.style.pointerEvents = "none";
    documentRoot.style.width = `${maxWidth}pt`;
    documentRoot.style.background = "#ffffff";
    documentRoot.innerHTML = `
      <style>
        .generated-document-pdf, .generated-document-pdf * { box-sizing: border-box; }
        .generated-document-pdf {
          color: #111827;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 11pt;
          line-height: 1.45;
          overflow-wrap: anywhere;
          text-align: justify;
          white-space: normal;
          padding-bottom: 14pt;
        }
        .generated-document-pdf * { line-height: 1.45 !important; }
        .generated-document-pdf p:first-child,
        .generated-document-pdf div:first-child { margin-top: 0 !important; }
        .generated-document-pdf p,
        .generated-document-pdf div { max-width: 100%; }
        .generated-document-pdf ul,
        .generated-document-pdf ol { padding-left: 18pt; }
        .generated-document-pdf table { border-collapse: collapse; max-width: 100%; }
        .generated-document-pdf img {
          max-width: 100% !important;
          height: auto !important;
          object-fit: contain;
        }
        .generated-document-pdf td,
        .generated-document-pdf th { border-color: #111827; }
      </style>
      <main class="generated-document-pdf">${interpolateHtml(template.content || "") || "<p>Documento sem conteúdo configurado.</p>"}</main>
    `;
    document.body.appendChild(documentRoot);
    try {
      await renderHtmlElementToPdf(doc, documentRoot, {
        x: margin,
        y,
        width: maxWidth,
        pageHeight,
        topMargin: margin,
        bottomMargin: 90,
        getNextPageStartY: () => drawHeader({ includeTitle: false }),
      });
    } finally {
      document.body.removeChild(documentRoot);
    }

    doc.setFont(undefined, "normal");
    addFooter();
    const fileName = `${preview ? "previa-" : ""}${field?.id || "documento"}-${instance?.id || "solicitacao"}.pdf`;
    if (!save) return { doc, fileName };
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    if (openedPdfWindow && !openedPdfWindow.closed) {
      openedPdfWindow.location.href = pdfUrl;
      pdfOpened = true;
    }
    doc.save(fileName);
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    return { doc, fileName };
  } finally {
    if (openedPdfWindow && !openedPdfWindow.closed && !pdfOpened) {
      openedPdfWindow.close();
    }
  }
};

export const generateDocumentPdfBatch = async ({ documents = [], company: providedCompany, openedPdfWindow, fileName = "documentos.pdf" } = {}) => {
  const items = Array.isArray(documents) ? documents.filter(Boolean) : [];
  if (!items.length) throw new Error("Selecione ao menos um modelo de documento.");
  let pdfOpened = false;
  try {
    let company = providedCompany || null;
    if (!company) {
      try { company = (await api.get("/company")).data; } catch { company = null; }
    }
    let doc = null;
    for (let index = 0; index < items.length; index += 1) {
      const result = await generateDocumentPdf({
        ...items[index],
        company,
        providedDocument: doc,
        addPageBefore: Boolean(doc),
        save: false,
        openedPdfWindow: null,
      });
      doc = result?.doc || doc;
    }
    if (!doc) throw new Error("Não foi possível montar o PDF combinado.");
    const normalizedFileName = String(fileName || "documentos.pdf").toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;
    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    if (openedPdfWindow && !openedPdfWindow.closed) { openedPdfWindow.location.href = pdfUrl; pdfOpened = true; }
    doc.save(normalizedFileName);
    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    return { doc, fileName: normalizedFileName, count: items.length };
  } finally {
    if (openedPdfWindow && !openedPdfWindow.closed && !pdfOpened) openedPdfWindow.close();
  }
};

/* document-pdf-batch-20260915 */

export default generateDocumentPdf;
